import Image from "next/image";
import Quote from "@/components/Quote";

export const feastAndFettleContent = (
  <>
    <div className="prose dark:prose-invert max-w-none text-text-muted-light dark:text-text-muted-dark">
      <Image
        src="/assets/work/ff-full-bleed.webp"
        alt="banner"
        width={1920}
        height={1080}
      />

      <h2>The Challenge</h2>
      <p>
        Feast & Fettle co-founders Maggie Mulvena and Nikki Oliveira, and CEO
        Carlos Ventura Jr. came to us in their second year of operation with the
        kind of challenge most entrepreneurs dream about: They were scaling so
        fast, their simple website built on a low-code ecommerce platform was
        holding them back.
      </p>
      <p>
        They needed a strategic partner to handle the product development
        workflows and help them make smart decisions about the technical
        elements undergirding their business.
      </p>
      <p>
        In the short term, our goal was to MacGyver their existing digital
        product in service of:
      </p>
      <ul>
        <li>Improving their customer experience</li>
        <li>Juicing up the back-end operations</li>
      </ul>
      <p>
        Once we&rsquo;d pushed their original system to its limit, Feast &
        Fettle asked us to create a fully customized solution that could help
        them scale without limits.
      </p>

      <h2>Insights</h2>
      <h3>Gourmet food demands white-glove service</h3>
      <p>
        Feast & Fettle delivers quality, craft, and care to a high-end
        clientele. Their discerning approach and commitment to delivering
        superior service needed to be matched in the customer UX, back-of-house
        tools, and every other facet of their digital product.
      </p>
      <p>
        Luckily, they found in us an equally meticulous and intentional product
        design partner.
      </p>
      <p>
        This overlap in our values meant we could deliver design and engineering
        outcomes that bring Feast & Fettle&rsquo;s exacting standards into every
        facet of their digital product.
      </p>

      <h2>Approach</h2>
      <p>
        The goal of our initial scope of work was to operationalize a sturdy 1.0
        system optimizing their existing tools and streamlining the user
        experience for core tasks like signing up and placing an order. It also
        delivered dramatic improvements in functionality for the kitchen staff
        and other back-of-house personnel.
      </p>
      <p>
        After finishing this initial set of system-wide updates, we worked in
        two-week sprints on a long-term engagement focusing on iterative
        upgrades to the 1.0 system.
      </p>
      <p>
        When their business surged during the pandemic, we were able to keep
        pace, adapt as their priorities changed, and deliver the solutions they
        needed to thrive under pressure - without sacrificing quality.
      </p>

      <h2>Solution</h2>
      <p>
        Through our sustained partnership, Township simplified the complex
        workflows of Feast & Fettle that enable the customizable, upmarket
        experience for more than 5,000 customers across New England.
      </p>
      <h3>Substitutions? Please!</h3>
      <p>
        Working dynamically with the Feast & Fettle team, we streamlined a
        fairly complex service offering into a very approachable experience for
        customers and staff alike. Meal customization is a perfect example.
      </p>
      <p>
        Feast & Fettle prides itself on offering meals that are
        &ldquo;meticulously crafted&rdquo; for each household. When customers
        place their order, they can customize details about each meal, add
        additional items to their menus, etc. — all in a simple, satisfying
        online experience.
      </p>
      <p>
        On the business side, each add-on or customization is an opportunity for
        additional revenue from existing customers. Making it easy to
        add/customize meals and orders, then ensuring these requests are
        executed by back-of-house personnel, meant better margins on each order
        and lower customer acquisition costs over time.
      </p>
      <p>
        To make these bottom-line boosting customizations happen, we developed
        systems and tools that handle the operational and logistical complexity
        of these changes and substitutions. Order data is piped into a series of
        exports and dashboards that power Feast & Fettle&rsquo;s back-of-house
        operations. We built features like subscription billing, route
        optimization, and inventory management to soup up this functionality
        over the course of our work together.
      </p>

      <h2>Results</h2>
      <h3>4,996% 3-Year Growth</h3>
      <p>
        In August 2022, Feast & Fettle was in the top 2% in Inc&rsquo;s list of
        the 5000 Fastest-Growing Private Companies in America by revenue. In the
        press release announcing this milestone, Feast & Fettle CEO (and project
        partner) Carlos Ventura attributed part of their success to the custom
        solution we built for them.
      </p>

      <Quote
        text="We were incredibly lucky to have already built the infrastructure for scale before the pandemic hit.... Our team was able to successfully manage demand without ever sacrificing on product quality."
        author="Carlos Ventura"
        role="Feast & Fettle CEO"
        type="client"
      />

      <h3>
        Like a perfect French omelet, the tech solution we built for Feast &
        Fettle was crafted with precision and technical finesse.
      </h3>
      <p>
        Because the business was growing at breakneck speed, we served as their
        product team until they were ready to establish an in-house team. By
        working with care and consideration on the cores systems that power and
        sustain their product, we created sustainable solutions and long-term
        value for Feast & Fettle&rsquo;s user experience, brand, and business.
      </p>

      <h2>Making an impact</h2>
      <p>
        As a business, Township values working with clients who are creating
        useful products and services that make people&rsquo;s lives demonstrably
        better. To us, there&rsquo;s no better example of this than Feast &
        Fettle. Through their success, they&rsquo;ve consistently invested in
        growing locally to meet demand. They&rsquo;ve expanded their facilities
        in East Providence and are building a second, state-of-the-art
        commercial kitchen in Connecticut, creating jobs in these communities.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <video
          muted
          controls
          className="w-full"
          src="/assets/work/ff-video-1.mp4"
        />
        <video
          muted
          controls
          className="w-full"
          src="/assets/work/ff-video-2.mp4"
        />
      </div>
    </div>
  </>
);
